import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import ReportDocument from '../components/devreport/presentation/ReportDocument';
import { buildPresentation } from '../components/devreport/presentation/presentation';
import { assetTracker } from '../components/devreport/presentation/assetTracker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Bare A4 print route — no app chrome. Reused by browser Print/Save-as-PDF and
 * by the server-side Playwright PDF render (which injects the auth token into
 * localStorage before navigating and waits for window.__PDF_READY__).
 */
const DevelopmentReportPrint = () => {
  const { reportId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let done = false;
    assetTracker.reset();
    (async () => {
      try {
        // Playwright injects the token via addInitScript → localStorage; make
        // sure axios carries it even before AuthContext boots.
        const tok = localStorage.getItem('token');
        if (tok) axios.defaults.headers.common['Authorization'] = `Bearer ${tok}`;
        const res = await axios.get(`${API}/development-reports/${reportId}`);
        if (!done) setData(res.data);
      } catch (e) {
        if (!done) { setError(e.response?.data?.detail || 'Failed to load report'); window.__PDF_ERROR__ = true; }
      }
    })();
    return () => { done = true; };
  }, [reportId]);

  // Deterministic asset-ready: wait for React render + all AuthImg assets to
  // settle (loaded or failed→fallback), capped, before signalling Playwright.
  useEffect(() => {
    if (!data) return undefined;
    let done = false;
    const start = Date.now();
    const CAP = 12000;
    // Give children a tick to mount and register their pending assets.
    const timer = setInterval(() => {
      const waitedMin = Date.now() - start > 500;
      const settled = assetTracker.pending() === 0;
      const capped = Date.now() - start > CAP;
      if (!done && waitedMin && (settled || capped)) {
        done = true;
        clearInterval(timer);
        window.__PDF_BROKEN_ASSETS__ = assetTracker.broken();
        window.__PDF_READY__ = true;
      }
    }, 150);
    return () => clearInterval(timer);
  }, [data]);

  if (error) return <div style={{ padding: 40, color: '#b91c1c' }} data-testid="print-error">{error}</div>;
  if (!data) return <div style={{ padding: 40, color: '#94a3b8' }}>Loading…</div>;

  const pres = buildPresentation(data);
  return (
    <div data-testid="report-print-root">
      <ReportDocument report={pres.report} sections={pres.sections} overview={pres.overview} lead={pres.lead} printMode />
    </div>
  );
};

export default DevelopmentReportPrint;
